import { useEffect, useRef } from "react";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useProfileDraft } from "@/context/ProfileDraftContext";
import { Sparkles, Loader2, RotateCcw, ArrowRight } from "lucide-react";

export function DesignNavigationGuardDialog() {
  const {
    isNavigationGuardOpen,
    isApplyingAndNavigating,
    confirmApplyAndNavigate,
    confirmDiscardAndNavigate,
    cancelNavigation,
  } = useProfileDraft();

  const applyButtonRef = useRef<HTMLButtonElement | null>(null);

  // Focus the primary action when dialog opens
  useEffect(() => {
    if (isNavigationGuardOpen) {
      setTimeout(() => {
        applyButtonRef.current?.focus();
      }, 50);
    }
  }, [isNavigationGuardOpen]);

  // Escape key handler ensures Escape strictly behaves as "Stay Editing"
  useEffect(() => {
    if (!isNavigationGuardOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isApplyingAndNavigating) {
        e.preventDefault();
        cancelNavigation();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isNavigationGuardOpen, isApplyingAndNavigating, cancelNavigation]);

  if (!isNavigationGuardOpen) return null;

  return (
    <Dialog
      open={isNavigationGuardOpen}
      onOpenChange={(open) => {
        if (!open && !isApplyingAndNavigating) {
          cancelNavigation();
        }
      }}
      title="Unapplied Design Customizations"
      description="You have transient design changes that have not been applied to your profile draft."
      className="max-w-md"
    >
      <div className="space-y-4 pt-1">
        <div className="rounded-lg bg-amber-50 dark:bg-amber-950/40 p-3.5 border border-amber-200 dark:border-amber-800/60 text-xs text-amber-900 dark:text-amber-200 flex items-start gap-2.5">
          <Sparkles className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
          <div className="leading-relaxed">
            <span className="font-semibold">Apply &amp; Continue</span> will commit your design customizations to the portfolio draft and persist them to the server before navigating.
          </div>
        </div>

        <div className="flex flex-col sm:flex-row-reverse items-stretch sm:items-center gap-2 pt-2">
          {/* Primary: Apply & Continue */}
          <Button
            ref={applyButtonRef}
            onClick={() => confirmApplyAndNavigate()}
            disabled={isApplyingAndNavigating}
            className="h-10 sm:h-9 bg-primary text-primary-foreground hover:bg-primary/90 font-medium text-xs gap-1.5 shadow-xs"
          >
            {isApplyingAndNavigating ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <ArrowRight className="h-3.5 w-3.5" />
            )}
            <span>{isApplyingAndNavigating ? "Applying & Saving..." : "Apply & Continue"}</span>
          </Button>

          {/* Secondary: Discard & Continue */}
          <Button
            variant="outline"
            onClick={confirmDiscardAndNavigate}
            disabled={isApplyingAndNavigating}
            className="h-10 sm:h-9 text-xs text-destructive hover:bg-destructive/10 hover:text-destructive border-destructive/30 gap-1.5"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            <span>Discard &amp; Continue</span>
          </Button>

          {/* Tertiary: Stay Editing */}
          <Button
            variant="ghost"
            onClick={cancelNavigation}
            disabled={isApplyingAndNavigating}
            className="h-10 sm:h-9 text-xs text-muted-foreground hover:text-foreground"
          >
            Stay Editing
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
